import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useCustomerView } from '@/hooks/useCustomerView';
import { useImageCache } from '@/context/ImageCacheContext';
import { ShoppingCart, Plus, Share2, Pencil, Upload, X, Crop, Check, Flag, RotateCw, ZoomIn, Wand2, Handshake, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { MrpEffectiveDate } from '@/components/MrpChangeBadge';
import html2canvas from 'html2canvas';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ImageLightbox from './ImageLightbox';
import AIStudioModal from './AIStudioModal';
import RelatedParts from './RelatedParts';
import PriceRequestDialog from './PriceRequestDialog';
import StockBadge from './StockBadge';
import { formatOeDisplay , formatPartNo, getDisplayPartNo } from '@/utils/partDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin mobile numbers for checking admin status
const ADMIN_MOBILE_NUMBERS = ["9999313175", "9811117751", "9643558419"];

const PartCard = ({ part, onProductUpdated, autoOpenEdit = false }) => {
  const { addToCart } = useCart();
  const { user, token } = useAuth();
  const { getCachedImage, fetchImage, clearCache } = useImageCache();
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [quantity, setQuantity] = useState(Math.max(1, parseInt(part.moq, 10) || 1));
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editForm, setEditForm] = useState({});
  const [marking, setMarking] = useState(false);
  const [isMarked, setIsMarked] = useState(part.marked_for_review || false);
  const [markReasons, setMarkReasons] = useState(part.mark_reasons || []);
  const [showMarkDialog, setShowMarkDialog] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [showLightbox, setShowLightbox] = useState(false);
  // Multi-select category dropdown state (matches AdminProducts UX)
  const [showEditCategoryDropdown, setShowEditCategoryDropdown] = useState(false);
  const [editCategorySearch, setEditCategorySearch] = useState('');
  const cardRef = useRef(null);
  
  // Price Request states
  const [showPriceRequestDialog, setShowPriceRequestDialog] = useState(false);
  const [canRequestPrice, setCanRequestPrice] = useState(false);
  const [specialPrice, setSpecialPrice] = useState(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  
  // Image editing states
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [newImageFile, setNewImageFile] = useState(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [crop, setCrop] = useState(null); // Will be set by onImageLoad
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [showAIStudio, setShowAIStudio] = useState(false);
  const [aiStudioImage, setAIStudioImage] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Check if current user is admin, staff, or critic
  // Customer View toggle masks admin/staff identity so an admin can preview the
  // app exactly as a customer would see it (for recording videos / QA).
  const { isCustomerView } = useCustomerView();
  const rawIsAdmin = user && ADMIN_MOBILE_NUMBERS.includes(user.mobile);
  const rawIsStaff = user?.is_staff_user || false;
  const rawIsCritic = user?.is_critic || false;
  const isAdmin = rawIsAdmin && !isCustomerView;
  const isStaffUser = rawIsStaff && !isCustomerView;
  const isCritic = rawIsCritic && !isCustomerView;
  const canMarkProducts = isAdmin || isCritic;
  // Staff users can edit photos everywhere
  const canEditPhotos = isAdmin || isStaffUser;
  
  // Check if user is from India (for GST label display)
  // Show GST label only for Indian customers, not for Nepal/Bangladesh
  const isIndianCustomer = user?.country === 'India' || 
    (!user?.country && user?.state && !['Nepal', 'Bangladesh'].includes(user?.country));

  // Check price request eligibility and special prices
  useEffect(() => {
    const checkPriceEligibility = async () => {
      if (!token || isAdmin || isStaffUser) return;
      
      try {
        // Check eligibility
        const eligRes = await axios.get(`${API}/price-requests/check-eligibility`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCanRequestPrice(eligRes.data.can_request_special_price);
        
        // ALWAYS fetch special prices - customer might have them even if they can't request new ones
        try {
          const pricesRes = await axios.get(`${API}/price-requests/my-special-prices`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const prices = pricesRes.data.special_prices || {};
          if (prices[part.jp_part_no]) {
            setSpecialPrice(prices[part.jp_part_no].special_price);
          }
        } catch (priceError) {
          console.error('Error fetching special prices:', priceError);
        }
        
        // Check for pending requests (only if can request)
        if (eligRes.data.can_request_special_price) {
          const requestsRes = await axios.get(`${API}/price-requests/my-requests`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const pending = (requestsRes.data.requests || []).find(
            r => r.jp_part_no === part.jp_part_no && ['pending', 'counter_offered'].includes(r.status)
          );
          setHasPendingRequest(!!pending);
        }
      } catch (error) {
        console.error('Error checking price eligibility:', error);
      }
    };
    
    checkPriceEligibility();
  }, [token, part.jp_part_no, isAdmin, isStaffUser]);

  // Optimized image loading using batch cache
  useEffect(() => {
    setImageLoading(true);
    
    if (part.image) {
      // Check cache first
      const cached = getCachedImage(part.image);
      if (cached) {
        setImageDataUrl(cached);
        setImageLoading(false);
        return;
      }
      
      // Fetch image (will be batched with other requests)
      fetchImage(part.image).then(dataUrl => {
        if (dataUrl) {
          setImageDataUrl(dataUrl);
        }
        setImageLoading(false);
      });
    } else {
      setImageDataUrl(null);
      setImageLoading(false);
    }
  }, [part.image, part.jp_part_no, getCachedImage, fetchImage]);

  // Load categories for edit dialog — cached globally to avoid N+1 calls per PartCard
  useEffect(() => {
    if (!canEditPhotos) return;
    if (window.__jp_categories_cache && Date.now() - window.__jp_categories_cache_ts < 300000) {
      setCategories(window.__jp_categories_cache);
      return;
    }
    if (window.__jp_categories_loading) {
      const wait = setInterval(() => {
        if (window.__jp_categories_cache) {
          setCategories(window.__jp_categories_cache);
          clearInterval(wait);
        }
      }, 200);
      return () => clearInterval(wait);
    }
    window.__jp_categories_loading = true;
    axios.get(`${API}/categories`)
      .then(response => {
        const cats = response.data?.categories || response.data || [];
        const categoryNames = (Array.isArray(cats) ? cats : []).map(cat => typeof cat === 'string' ? cat : cat.name).filter(Boolean);
        window.__jp_categories_cache = categoryNames;
        window.__jp_categories_cache_ts = Date.now();
        setCategories(categoryNames);
      })
      .catch(error => console.error('Error loading categories:', error))
      .finally(() => { window.__jp_categories_loading = false; });
  }, [canEditPhotos]);

  // Auto-open the Edit dialog when the parent passes `autoOpenEdit` —
  // used by the Missing-Images panel to deep-link a staff user straight
  // into the existing image-upload + AI-polish workflow without sending
  // them to a separate page.
  useEffect(() => {
    if (!autoOpenEdit || !canEditPhotos) return;
    if (showEditDialog) return;
    // Synthesise a click event so handleEdit's stopPropagation is safe
    handleEdit({ stopPropagation: () => {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenEdit, canEditPhotos]);

  const handleAddToCart = () => {
    // If customer has a special price, use that instead of the general discounted_price
    const cartItem = specialPrice 
      ? { ...part, discounted_price: specialPrice, has_special_price: true }
      : part;
    // Cart context snaps to MOQ multiples and warns the user if needed,
    // so we can hand off the raw qty here without re-validating.
    addToCart(cartItem, quantity);
    setShowDialog(false);
    setQuantity(Math.max(1, parseInt(part.moq, 10) || 1));
  };

  // Admin: Open edit dialog
  const handleEdit = (e) => {
    e.stopPropagation();
    // Merge `category` (legacy string) and `categories` (array) so the dialog
    // shows every category currently assigned to this part — including those
    // assigned via the AdminProducts multi-select editor.
    const existingArr = Array.isArray(part.categories) ? part.categories.filter(Boolean) : [];
    const legacyCat = (part.category || '').trim();
    const initialCategories = legacyCat && legacyCat !== 'All Products' && !existingArr.includes(legacyCat)
      ? [legacyCat, ...existingArr]
      : existingArr;
    setEditForm({
      jp_part_no: part.jp_part_no,
      oe_part_no: part.oe_part_no || '',
      part_name: part.part_name,
      hsn_code: part.hsn_code || '',
      mrp: part.mrp,
      uom: part.uom || '',
      weight_kg: part.weight_kg ?? '',
      categories: initialCategories
    });
    setEditCategorySearch('');
    setShowEditCategoryDropdown(false);
    // Set the current image for editing
    setEditImageUrl(imageDataUrl);
    setNewImageFile(null);
    setShowEditDialog(true);
  };

  // Toggle a category in the edit multi-select
  const toggleEditCategory = (cat) => {
    const current = editForm.categories || [];
    if (current.includes(cat)) {
      setEditForm({ ...editForm, categories: current.filter(c => c !== cat) });
    } else {
      setEditForm({ ...editForm, categories: [...current, cat] });
    }
  };

  const filteredEditCategories = (categories || []).filter(cat =>
    cat.toLowerCase().includes(editCategorySearch.toLowerCase())
  );

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setShowCropDialog(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Callback when image loads in crop dialog
  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
    // Set initial crop to 90% of the image (free-form, not square)
    const { width, height } = e.currentTarget;
    setCrop({
      unit: 'px',
      width: width * 0.9,
      height: height * 0.9,
      x: width * 0.05,
      y: height * 0.05
    });
  }, []);

  // Generate cropped image
  const getCroppedImage = useCallback(() => {
    if (!imgRef.current || !completedCrop) return null;

    const canvas = document.createElement('canvas');
    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL('image/jpeg', 0.9);
  }, [completedCrop]);

  // Apply crop
  const handleApplyCrop = async () => {
    const croppedDataUrl = getCroppedImage();
    if (croppedDataUrl) {
      setEditImageUrl(croppedDataUrl);
      setNewImageFile(true); // Mark that we have a new image to upload
      toast.success('Image cropped successfully');
    }
    setShowCropDialog(false);
    setImageToCrop(null);
    setCrop(null);
    setCompletedCrop(null);
  };

  // Cancel crop
  const handleCancelCrop = () => {
    setShowCropDialog(false);
    setImageToCrop(null);
    setCrop(null);
    setCompletedCrop(null);
  };

  // Remove image
  const handleRemoveImage = () => {
    setEditImageUrl(null);
    setNewImageFile('remove'); // Mark for removal
  };

  // Rotate image by 90 degrees
  const handleRotateImage = async () => {
    if (!editImageUrl || isRotating) return;
    
    setIsRotating(true);
    
    try {
      // Create an image element
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = editImageUrl;
      });
      
      // Create canvas and rotate
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Swap width and height for 90 degree rotation
      canvas.width = img.height;
      canvas.height = img.width;
      
      // Move to center, rotate, and draw
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      
      // Get the rotated image as data URL
      const rotatedDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Update the preview
      setEditImageUrl(rotatedDataUrl);
      setNewImageFile(true); // Mark that we have a new image to upload
      
      toast.success('Image rotated 90°');
    } catch (error) {
      console.error('Rotation error:', error);
      toast.error('Failed to rotate image');
    } finally {
      setIsRotating(false);
    }
  };

  // Admin: Save product edits with image
  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // URL encode the part number for all API calls
      // Double-encode slashes to prevent nginx from treating them as path separators
      const encodedPartNo = encodeURIComponent(part.jp_part_no).replace(/%2F/gi, '%252F');

      // 2026-06-18: sanitize the payload so existing parts without
      // `uom` / `weight_kg` can still be saved when only MRP / name
      // changes. The PartUpdate Pydantic model expects either a valid
      // UOM literal or null — empty strings would 422.
      const buildPayload = (extra = {}) => {
        const p = { ...editForm, ...extra };
        if (!p.uom) {
          delete p.uom;
        }
        if (p.weight_kg === '' || p.weight_kg == null) {
          delete p.weight_kg;
        } else {
          p.weight_kg = Number(p.weight_kg);
        }
        return p;
      };

      // First update the product details
      await axios.put(`${API}/admin/products/${encodedPartNo}`, buildPayload(), {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Clear old image cache - clear ALL cached images for this part number
      // This handles cases where part number contains special characters
      const partNoBase = part.jp_part_no.split('/')[0].split('_')[0]; // Get base part number
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('img_') && (key.includes(part.jp_part_no) || key.includes(partNoBase))) {
          localStorage.removeItem(key);
        }
      }
      if (part.image) {
        localStorage.removeItem(`img_${part.image}`);
      }

      // Handle image changes
      if (newImageFile === 'remove') {
        // User wants to remove the image - update product with no image
        await axios.put(`${API}/admin/products/${encodedPartNo}`,
          buildPayload({ image: '' }),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setImageDataUrl(null);
      } else if (newImageFile && editImageUrl && editImageUrl.startsWith('data:')) {
        // New image to upload (editImageUrl contains base64 data)
        const uploadResponse = await axios.post(`${API}/admin/products/${encodedPartNo}/image`, 
          { image_data: editImageUrl },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // The server returns the new filename - use it immediately
        const newFilename = uploadResponse.data?.image;
        if (newFilename) {
          console.log('Image uploaded, new filename:', newFilename);
          // Clear ALL cached images for this part
          for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i);
            if (key && key.startsWith(`img_${part.jp_part_no}`)) {
              localStorage.removeItem(key);
            }
          }
          // Set the new image immediately so user sees it
          setImageDataUrl(editImageUrl);
        }
      }

      toast.success('Product updated successfully');
      setShowEditDialog(false);
      setNewImageFile(null);
      
      // Notify parent to refresh the product list - this is CRITICAL
      // Without this, the part.image prop won't update to the new filename
      if (onProductUpdated) {
        onProductUpdated();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update product';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    
    if (sharing) return;
    setSharing(true);

    try {
      // Create a shareable card element
      const shareCard = document.createElement('div');
      shareCard.style.cssText = `
        width: 350px;
        background: white;
        border-radius: 12px;
        overflow: hidden;
        font-family: Arial, sans-serif;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      `;

      // Build the price HTML - NO discount percentage shown to customers
      const priceHtml = part.discount_percentage > 0 ? `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="color: #666; font-size: 13px;">MRP:</span>
          <span style="text-decoration: line-through; color: #999; font-size: 16px;">₹${part.mrp.toFixed(2)}</span>
        </div>
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #16a34a; font-size: 13px; font-weight: 600;">Your Price:</span>
          <span style="color: #dc2626; font-size: 24px; font-weight: bold;">₹${part.discounted_price.toFixed(2)}</span>
        </div>
      ` : `
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <span style="color: #666; font-size: 13px;">MRP:</span>
          <span style="color: #dc2626; font-size: 24px; font-weight: bold;">₹${part.mrp.toFixed(2)}</span>
        </div>
      `;

      // Get the logo URL
      const logoUrl = window.location.origin + '/jp_logo.png';

      shareCard.innerHTML = `
        <div style="position: relative; background: #f8f9fa; padding: 20px; display: flex; align-items: center; justify-content: center; min-height: 200px;">
          ${imageDataUrl ? `<img src="${imageDataUrl}" style="max-width: 100%; max-height: 180px; object-fit: contain;" />` : '<div style="color: #999; font-size: 14px;">No Image</div>'}
        </div>
        <div style="padding: 16px;">
          <h3 style="font-size: 16px; font-weight: bold; color: #1f2937; margin: 0 0 12px 0; line-height: 1.3;">
            ${part.part_name}
          </h3>
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
            <span style="color: #666; font-size: 12px;">Part No:</span>
            <span style="color: #dc2626; font-weight: bold; font-size: 14px;">${formatPartNo(part)}</span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 12px;">
            <span style="color: #666; font-size: 12px;">OE:</span>
            <span style="color: #374151; font-weight: 500; font-size: 13px;">${formatOeDisplay(part.oe_part_no, true)}</span>
          </div>
          <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 12px;">
            ${priceHtml}
          </div>
          <div style="margin-top: 12px; display: flex; align-items: center; justify-content: center; gap: 8px;">
            <img src="${logoUrl}" style="height: 28px; width: auto;" onerror="this.style.display='none'" />
            <span style="color: #dc2626; font-weight: bold; font-size: 14px;">Jai Paras Spares</span>
          </div>
        </div>
      `;

      document.body.appendChild(shareCard);

      // Generate product link
      const productLink = `${window.location.origin}/catalog?search=${encodeURIComponent(part.new_jp_part_no || part.jp_part_no)}`;

      // Capture the card as image
      const canvas = await html2canvas(shareCard, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });

      document.body.removeChild(shareCard);

      // Convert to blob
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')),
          'image/png', 0.9
        );
      });
      const displayNo = part.new_jp_part_no || part.jp_part_no;
      const file = new File([blob], `${displayNo}.png`, { type: 'image/png' });

      // Share with native API if available
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `${part.part_name} - Jai Paras Spares`,
            text: `Check out this product from Jai Paras Spares!\n\n${part.part_name}\nPart No: ${displayNo}\nPrice: ₹${(part.discounted_price || part.mrp).toFixed(2)}\n\nTo order click here - ${productLink}`,
            files: [file],
          });
          toast.success('Shared successfully!');
        } catch (shareErr) {
          if (shareErr.name !== 'AbortError') {
            // Fallback on share failure
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${displayNo}.png`;
            link.click();
            URL.revokeObjectURL(url);
            const text = `*${part.part_name}*\n\nPart No: ${displayNo}\n${formatOeDisplay(part.oe_part_no)}\nPrice: Rs.${(part.discounted_price || part.mrp).toFixed(2)}\n\n*Jai Paras Spares*\n\nTo order: ${productLink}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
            toast.success('Image downloaded! Share via WhatsApp');
          }
        }
      } else {
        // Fallback: Download image and open WhatsApp
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${displayNo}.png`;
        link.click();
        URL.revokeObjectURL(url);
        
        const text = `*${part.part_name}*\n\nPart No: ${displayNo}\n${formatOeDisplay(part.oe_part_no)}\nPrice: Rs.${(part.discounted_price || part.mrp).toFixed(2)}\n\n*Jai Paras Spares*\n\nTo order: ${productLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        toast.success('Image downloaded! Share via WhatsApp');
      }
    } catch (error) {
      console.error('Share error:', error);
      if (error.name !== 'AbortError') {
        // Final fallback: text-only share
        const productLink = `${window.location.origin}/catalog?search=${encodeURIComponent(part.new_jp_part_no || part.jp_part_no)}`;
        const displayNo = part.new_jp_part_no || part.jp_part_no;
        const text = `*${part.part_name}*\n\nPart No: ${displayNo}\nPrice: Rs.${(part.discounted_price || part.mrp).toFixed(2)}\n\nTo order: ${productLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
        toast.info('Sharing text only - image generation not supported on this device.');
      }
    } finally {
      setSharing(false);
    }
  };

  // Handle mark/unmark product for review
  const handleMarkForReview = (e) => {
    e.stopPropagation();
    if (marking) return;
    
    if (isMarked) {
      // Unmark directly
      submitUnmark();
    } else {
      // Show dialog to select reasons
      setSelectedReasons([]);
      setShowMarkDialog(true);
    }
  };

  // Submit mark with reasons
  const submitMark = async () => {
    if (selectedReasons.length === 0) {
      toast.error('Please select at least one reason');
      return;
    }
    
    setMarking(true);
    setShowMarkDialog(false);
    
    try {
      const encodedPartNo = encodeURIComponent(part.jp_part_no).replace(/%2F/gi, '%252F');
      await axios.post(`${API}/admin/products/${encodedPartNo}/mark`, 
        { reasons: selectedReasons },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setIsMarked(true);
      setMarkReasons(selectedReasons);
      toast.success('Product marked for review');
      
      if (onProductUpdated) {
        onProductUpdated();
      }
    } catch (error) {
      toast.error('Failed to mark product');
    } finally {
      setMarking(false);
    }
  };

  // Submit unmark
  const submitUnmark = async () => {
    setMarking(true);
    
    try {
      const encodedPartNo = encodeURIComponent(part.jp_part_no).replace(/%2F/gi, '%252F');
      await axios.post(`${API}/admin/products/${encodedPartNo}/unmark`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIsMarked(false);
      setMarkReasons([]);
      toast.success('Product unmarked');
      
      if (onProductUpdated) {
        onProductUpdated();
      }
    } catch (error) {
      toast.error('Failed to unmark product');
    } finally {
      setMarking(false);
    }
  };

  // Toggle reason selection
  const toggleReason = (reason) => {
    setSelectedReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  return (
    <>
      <div
        ref={cardRef}
        className="product-card bg-white border border-border rounded-sm overflow-hidden hover:border-primary/50 transition-all duration-200 relative"
        data-testid={`part-card-${part.jp_part_no}`}
      >
        {/* Action Buttons - For Admins (full), Staff (photo edit), or Critics (mark only) */}
        {(isAdmin || isStaffUser || isCritic) && (
          <div className="absolute top-2 right-2 z-10 flex gap-1">
            {/* Mark for Review Button - Available ONLY for Admin and Critic (NOT customers or staff) */}
            {canMarkProducts && (
              <button
                onClick={handleMarkForReview}
                disabled={marking}
                className={`${isMarked ? 'bg-orange-100 border-orange-300' : 'bg-white/90 border-gray-200'} hover:bg-orange-50 border rounded-full p-2 shadow-sm transition-all`}
                title={isMarked ? "Unmark Product" : "Mark for Review"}
                data-testid="mark-product-btn"
              >
                <Flag className={`w-4 h-4 ${marking ? 'animate-pulse text-gray-400' : isMarked ? 'text-orange-600 fill-orange-600' : 'text-orange-500'}`} />
              </button>
            )}
            {/* Admin/Staff buttons - Photo editing available to staff */}
            {canEditPhotos && (
              <>
                {/* Edit Button - For photo editing (staff) or full edit (admin) */}
                <button
                  onClick={handleEdit}
                  className="bg-white/90 hover:bg-blue-50 border border-gray-200 rounded-full p-2 shadow-sm transition-all"
                  title="Edit Product"
                  data-testid="edit-product-btn"
                >
                  <Pencil className="w-4 h-4 text-blue-600" />
                </button>
              </>
            )}
            {/* Admin-only Share button */}
            {isAdmin && (
              <>
                {/* Share Button - Admin Only */}
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  className="bg-white/90 hover:bg-green-50 border border-gray-200 rounded-full p-2 shadow-sm transition-all"
                  title="Share"
                  data-testid="share-product-btn"
                >
                  <Share2 className={`w-4 h-4 ${sharing ? 'text-gray-400 animate-pulse' : 'text-green-600'}`} />
                </button>
              </>
            )}
          </div>
        )}

        {/* Marked for Review Badge - Only visible to Admin/Staff/Critic, NOT customers */}
        {isMarked && canMarkProducts && (
          <div className="absolute top-2 left-2 z-10">
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1" title={markReasons.length > 0 ? markReasons.join(', ') : 'Marked for review'}>
              <Flag className="w-3 h-3 fill-white" />
              {markReasons.length > 0 ? markReasons[0] : 'Review'}
              {markReasons.length > 1 && <span className="text-[10px]">+{markReasons.length - 1}</span>}
            </span>
          </div>
        )}

        {/* Image - Reduced height, object-contain for full visibility - Click to expand */}
        <div 
          className="w-full h-32 sm:h-36 md:h-40 bg-gray-50 overflow-hidden relative flex items-center justify-center p-2 protected-image-container cursor-pointer group"
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => imageDataUrl && setShowLightbox(true)}
          title="Click to expand image"
        >
          {imageLoading ? (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
              Loading...
            </div>
          ) : imageDataUrl ? (
            <>
              <img
                src={imageDataUrl}
                alt={part.part_name}
                className="max-w-full max-h-full object-contain protected-image mx-auto block transition-transform group-hover:scale-105"
                style={{ margin: '0 auto', display: 'block' }}
                data-protected="true"
                draggable="false"
                onDragStart={(e) => e.preventDefault()}
                onContextMenu={(e) => e.preventDefault()}
              />
              {/* Zoom indicator on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded-full p-2">
                  <ZoomIn className="w-5 h-5 text-white" />
                </div>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
              No Image
            </div>
          )}
        </div>

        {/* Content - Compact padding */}
        <div className="p-2 sm:p-3">
          {/* Part Name */}
          <h3 className="font-semibold text-sm sm:text-base mb-2 line-clamp-2 min-h-[36px] sm:min-h-[40px] text-gray-900" title={part.part_name}>
            {part.part_name}
          </h3>

          {/* RC-211 (2026-08-06) — Live Stock badge. Customer sees just
              a green "In Stock" pill when total > 0; admin/staff sees
              the exact number with per-godown breakdown. Hidden when
              the JP is untracked. */}
          <div className="mb-2 min-h-[20px]">
            <StockBadge jpPartNo={part.jp_part_no} />
          </div>

          {/* Part Details — 2-line layout per product directive:
              Line 1: bare JP number (no bracket)
              Line 2: OE Ref if OE exists, else "Old Part No:" + legacy */}
          <div className="space-y-1 mb-2">
            <div className="flex justify-between items-start gap-1">
              <span className="text-xs text-gray-600 whitespace-nowrap">Part No:</span>
              <span className="text-sm sm:text-base font-bold part-number text-right text-red-600">{getDisplayPartNo(part)}</span>
            </div>
            {(() => {
              const hasOe = part.oe_part_no
                && String(part.oe_part_no).trim()
                && String(part.oe_part_no).trim().toLowerCase() !== 'not available';
              const legacy = part.legacy_jp_part_no || part.old_jp_part_no
                || (part.new_jp_part_no && part.new_jp_part_no !== part.jp_part_no ? part.jp_part_no : '');
              if (hasOe) {
                return (
                  <div className="flex justify-between items-start gap-1">
                    <span className="text-xs text-gray-600 whitespace-nowrap shrink-0">OE:</span>
                    <span className="text-xs sm:text-sm font-medium part-number text-right truncate max-w-[70%] text-gray-800">{formatOeDisplay(part.oe_part_no, true)}</span>
                  </div>
                );
              }
              if (legacy) {
                return (
                  <div className="flex justify-between items-start gap-1">
                    <span className="text-xs text-gray-600 whitespace-nowrap shrink-0">Old Part No:</span>
                    <span className="text-xs sm:text-sm font-medium part-number text-right truncate max-w-[70%] text-gray-800">{legacy}</span>
                  </div>
                );
              }
              return null;
            })()}
            {part.brand_part_number && (
              <div className="flex justify-between items-start gap-1" data-testid="brand-part-number-row">
                <span className="text-xs text-gray-600 whitespace-nowrap shrink-0">Brand Part No:</span>
                <span className="text-xs sm:text-sm font-semibold part-number text-right truncate max-w-[70%] text-blue-700">{part.brand_part_number}</span>
              </div>
            )}
          </div>

          {/* Pricing Section - Shows special price if available */}
          {specialPrice ? (
            // Customer has special price for this part
            <div className="bg-amber-50 border border-amber-300 rounded p-2 mb-2">
              <div className="flex items-center gap-1 mb-1">
                <Handshake className="w-3 h-3 text-amber-600" />
                <span className="text-xs font-bold text-amber-700">Special Price</span>
              </div>
              <div className="space-y-0.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500 line-through">MRP: ₹{part.mrp.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-amber-700 font-medium">Spl Price:</span>
                  <div className="text-right">
                    <span className="text-base sm:text-lg font-bold text-amber-700">₹{specialPrice.toFixed(2)}</span>
                    {isIndianCustomer && (
                      <p className="text-[10px] text-gray-400">(Incl. 18% GST)</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : part.discount_percentage > 0 ? (
            <div className="bg-green-50 border border-green-200 rounded p-2 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-green-700 bg-green-200 px-1.5 py-0.5 rounded">
                  Special Price
                </span>
                {/* Price Request Button */}
                {canRequestPrice && !hasPendingRequest && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowPriceRequestDialog(true); }}
                    className="p-1 hover:bg-amber-100 rounded transition-colors"
                    title="Request Special Price"
                  >
                    <Handshake className="w-4 h-4 text-amber-600" />
                  </button>
                )}
                {hasPendingRequest && (
                  <span className="text-xs text-amber-600 font-medium">Request Pending</span>
                )}
              </div>
              <div className="space-y-0.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-700 font-medium">MRP:</span>
                  <span className="text-sm font-bold text-gray-800">₹{part.mrp.toFixed(2)}</span>
                </div>
                <div className="flex justify-end">
                  <MrpEffectiveDate part={part} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-700 font-medium">Price:</span>
                  <div className="text-right">
                    <span className="text-base sm:text-lg font-bold text-red-600">₹{part.discounted_price.toFixed(2)}</span>
                    {isIndianCustomer && (
                      <p className="text-[10px] text-gray-400">(Incl. 18% GST)</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-700 font-medium">MRP:</span>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <span className="text-base sm:text-lg font-bold text-red-600">₹{part.mrp.toFixed(2)}</span>
                    {isIndianCustomer && (
                      <p className="text-[10px] text-gray-400">(Incl. 18% GST)</p>
                    )}
                    <div><MrpEffectiveDate part={part} /></div>
                  </div>
                  {/* Price Request Button */}
                  {canRequestPrice && !hasPendingRequest && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowPriceRequestDialog(true); }}
                      className="p-1 hover:bg-amber-100 rounded transition-colors"
                      title="Request Special Price"
                    >
                      <Handshake className="w-4 h-4 text-amber-600" />
                    </button>
                  )}
                  {hasPendingRequest && (
                    <span className="text-xs text-amber-600 font-medium">Pending</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* JP MRP change badge removed 2026-05-30 — operator confirmed
              new MRP + "Effective from <date>" alone is enough; the
              red/green old-vs-new badge was creating noise. */}

          <Button
            size="sm"
            onClick={() => setShowDialog(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded h-8 text-xs sm:text-sm font-semibold"
            data-testid={`add-to-cart-${part.jp_part_no}`}
          >
            <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            Add to Cart
          </Button>
        </div>
      </div>

      {/* Add to Cart Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md rounded-md bg-white border-2 border-primary shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">Add to Cart</DialogTitle>
            <DialogDescription className="text-left text-foreground">
              <span className="font-semibold text-base">{part.part_name}</span>
              <br />
              <span className="text-xs part-number text-muted-foreground">JP: {formatPartNo(part)}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="quantity" className="text-right font-semibold">
                Quantity
              </Label>
              <div className="col-span-3">
                <Input
                  id="quantity"
                  type="number"
                  min={Math.max(1, parseInt(part.moq, 10) || 1)}
                  // MOQ-aware step (2026-07-02): arrow-buttons + keyboard
                  // Up/Down step by the pack size so 12 → 24 → 36 …
                  // Typed values that aren't multiples get snapped UP on
                  // blur (below), and also as a final safety net inside
                  // addToCart. Both together satisfy the operator rule:
                  // "Whatever they type, next available MOQ multiple wins."
                  step={Math.max(1, parseInt(part.moq, 10) || 1)}
                  value={quantity}
                  onChange={(e) => {
                    const moq = Math.max(1, parseInt(part.moq, 10) || 1);
                    const raw = parseInt(e.target.value, 10);
                    if (Number.isNaN(raw)) {
                      setQuantity(moq);
                      return;
                    }
                    // Accept whatever the operator typed while they're
                    // still typing (no live snap — that would jump 1→12
                    // on the first keystroke). Blur handler snaps.
                    setQuantity(Math.max(1, raw));
                  }}
                  onBlur={(e) => {
                    const moq = Math.max(1, parseInt(part.moq, 10) || 1);
                    const raw = parseInt(e.target.value, 10);
                    if (Number.isNaN(raw) || raw < moq) {
                      setQuantity(moq);
                      return;
                    }
                    // Round UP to the next MOQ multiple. 40 with moq=12
                    // → 48. 20 with moq=12 → 24. Exact multiples pass
                    // through unchanged.
                    const snapped = raw % moq === 0
                      ? raw
                      : Math.ceil(raw / moq) * moq;
                    setQuantity(snapped);
                  }}
                  onKeyDown={(e) => {
                    // Enter also commits the snap so power-users don't
                    // have to click outside to see the adjusted value.
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  className="rounded-md border-2"
                  data-testid="quantity-input"
                />
                {parseInt(part.moq, 10) > 1 && (() => {
                  const moq = parseInt(part.moq, 10);
                  const q = parseInt(quantity, 10) || moq;
                  const willSnapTo = q < moq
                    ? moq
                    : (q % moq === 0 ? q : Math.ceil(q / moq) * moq);
                  const needsAdjust = willSnapTo !== q;
                  return (
                    <div className="mt-1.5 space-y-0.5">
                      <p className="text-xs text-amber-600 font-medium" data-testid="moq-hint">
                        Minimum order: <span className="font-bold">{moq} pcs</span> · sold in packs of {moq}
                      </p>
                      {needsAdjust && (
                        <p className="text-xs text-red-600 font-medium" data-testid="moq-snap-hint">
                          Will be adjusted to <span className="font-bold">{willSnapTo}</span> on Add to Cart
                          (next valid pack)
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            
            {part.discount_percentage > 0 ? (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right font-semibold">MRP -</Label>
                  <div className="col-span-3">
                    <p className="text-base font-semibold text-gray-400 line-through">₹{part.mrp.toFixed(2)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right font-semibold text-primary">Your Price</Label>
                  <div className="col-span-3">
                    <p className="text-xl font-bold text-primary">₹{part.discounted_price.toFixed(2)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right font-semibold">Total</Label>
                  <div className="col-span-3">
                    <p className="text-xl font-bold text-red-600">
                      ₹{(part.discounted_price * quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right font-semibold">Price</Label>
                  <div className="col-span-3">
                    <p className="text-xl font-bold text-primary">₹{part.mrp.toFixed(2)}</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right font-semibold">Total</Label>
                  <div className="col-span-3">
                    <p className="text-xl font-bold text-red-600">
                      ₹{(part.mrp * quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="rounded-md"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToCart}
              className="bg-primary hover:bg-primary/90 text-white rounded-md"
              data-testid="confirm-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Add to Cart
            </Button>
          </DialogFooter>
          
          {/* Related Parts Section */}
          <RelatedParts 
            jpPartNo={part.jp_part_no} 
            partName={part.part_name}
            className="mt-4"
          />
        </DialogContent>
      </Dialog>

      {/* Admin/Staff Edit Product Dialog - Staff can edit photos */}
      {canEditPhotos && (
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-2xl bg-white border-2 border-blue-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-900">Edit Product</DialogTitle>
              <DialogDescription>
                Update product details for {part.jp_part_no}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Image Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
                <Label className="text-gray-700 font-medium block mb-3">Product Image</Label>
                
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  {/* Image Preview */}
                  <div className="w-32 h-32 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                    {editImageUrl ? (
                      <img 
                        src={editImageUrl} 
                        alt={part.part_name} 
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm text-center px-2">No Image</span>
                    )}
                  </div>
                  
                  {/* Image Actions */}
                  <div className="flex flex-col gap-2 flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {editImageUrl ? 'Change Image' : 'Upload Image'}
                    </Button>
                    
                    {editImageUrl && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setAIStudioImage(editImageUrl);
                            setShowAIStudio(true);
                          }}
                          className="border-blue-400 text-blue-600 hover:bg-blue-50"
                        >
                          <Wand2 className="w-4 h-4 mr-2" />
                          AI Polish
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleRotateImage}
                          disabled={isRotating}
                          className="border-orange-300 text-orange-600 hover:bg-orange-50"
                        >
                          <RotateCw className={`w-4 h-4 mr-2 ${isRotating ? 'animate-spin' : ''}`} />
                          Rotate
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setImageToCrop(editImageUrl);
                            setShowCropDialog(true);
                          }}
                          className="border-green-300 text-green-600 hover:bg-green-50"
                        >
                          <Crop className="w-4 h-4 mr-2" />
                          Crop
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleRemoveImage}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      </>
                    )}
                    
                    {newImageFile && (
                      <p className="text-xs text-green-600 font-medium">
                        ✓ New image ready to upload
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Part Number - Read Only */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">Part No</Label>
                <Input
                  value={editForm.jp_part_no || ''}
                  disabled
                  className="col-span-3 bg-gray-100 text-gray-600"
                />
              </div>
              
              {/* OE Part Number */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">OE No</Label>
                <Input
                  value={editForm.oe_part_no || ''}
                  onChange={(e) => setEditForm({ ...editForm, oe_part_no: e.target.value })}
                  className="col-span-3 bg-white border-gray-300"
                  placeholder="Enter OE Part Number"
                />
              </div>
              
              {/* Part Name (read-only — edit in Total Products → HSN/Part Mgmt) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">Name</Label>
                <Input
                  value={editForm.part_name || ''}
                  readOnly
                  disabled
                  className="col-span-3 bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed"
                  placeholder="Locked"
                  title="Part name editing has moved to Total Products → Part Number / HSN Management"
                />
              </div>
              
              {/* MRP */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">MRP (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.mrp || ''}
                  onChange={(e) => setEditForm({ ...editForm, mrp: parseFloat(e.target.value) || 0 })}
                  className="col-span-3 bg-white border-gray-300"
                  placeholder="Enter MRP"
                />
              </div>

              {/* UOM (2026-06-17) — controlled vocabulary */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">UOM</Label>
                <select
                  value={editForm.uom || ''}
                  onChange={(e) => setEditForm({ ...editForm, uom: e.target.value })}
                  className="col-span-3 bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="edit-uom"
                >
                  <option value="">— Not set —</option>
                  <option value="PCS">PCS (Pieces)</option>
                  <option value="UNIT">UNIT (Units)</option>
                  <option value="NOS">NOS (Numbers)</option>
                  <option value="SET">SET (Set)</option>
                  <option value="KGS">KGS (Kilograms)</option>
                </select>
              </div>

              {/* Per-piece weight (kg) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">Weight (kg)</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  value={editForm.weight_kg ?? ''}
                  onChange={(e) => setEditForm({
                    ...editForm,
                    weight_kg: e.target.value === '' ? '' : parseFloat(e.target.value),
                  })}
                  className="col-span-3 bg-white border-gray-300"
                  placeholder="0.000"
                  data-testid="edit-weight-kg"
                />
              </div>
              
              {/* HSN Code (read-only — edit in Total Products → HSN Management) */}
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-700 font-medium">HSN Code</Label>
                <Input
                  value={editForm.hsn_code || ''}
                  readOnly
                  disabled
                  className="col-span-3 bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed"
                  placeholder="Locked"
                  title="HSN editing has moved to Total Products → HSN Management"
                  data-testid="edit-hsn-code"
                />
              </div>
              
              {/* Category - multi-select to match AdminProducts UX */}
              <div className="grid grid-cols-4 items-start gap-4">
                <Label className="text-right text-gray-700 font-medium mt-2">Categories</Label>
                <div className="col-span-3">
                  <div className="relative">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowEditCategoryDropdown(!showEditCategoryDropdown)}
                      className="w-full justify-between bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                      data-testid="edit-categories-trigger"
                    >
                      <span className="truncate text-left">
                        {editForm.categories?.length > 0
                          ? `${editForm.categories.length} categor${editForm.categories.length > 1 ? 'ies' : 'y'} selected`
                          : 'Select categories'}
                      </span>
                      <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0" />
                    </Button>
                    {showEditCategoryDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
                        <div className="p-2 border-b border-gray-100">
                          <Input
                            placeholder="Search categories..."
                            value={editCategorySearch}
                            onChange={(e) => setEditCategorySearch(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus
                            data-testid="edit-categories-search"
                          />
                        </div>
                        {editForm.categories?.length > 0 && (
                          <div className="p-2 border-b border-gray-100 flex flex-wrap gap-1">
                            {editForm.categories.map((cat) => (
                              <span key={cat} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                                {cat}
                                <X className="w-3 h-3 cursor-pointer hover:text-blue-900" onClick={() => toggleEditCategory(cat)} />
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="max-h-48 overflow-y-auto">
                          {filteredEditCategories.length === 0 ? (
                            <div className="p-2 text-sm text-gray-500 text-center">No categories found</div>
                          ) : (
                            filteredEditCategories.map((cat) => (
                              <button
                                key={cat}
                                type="button"
                                onClick={() => toggleEditCategory(cat)}
                                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-blue-50 transition-colors ${editForm.categories?.includes(cat) ? 'bg-blue-50 text-blue-700' : ''}`}
                                data-testid={`edit-category-option-${cat}`}
                              >
                                <div className={`w-4 h-4 border rounded flex items-center justify-center ${editForm.categories?.includes(cat) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                  {editForm.categories?.includes(cat) && <Check className="w-3 h-3 text-white" />}
                                </div>
                                {cat}
                              </button>
                            ))
                          )}
                        </div>
                        <div className="p-2 border-t border-gray-100">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setShowEditCategoryDropdown(false)}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid="edit-categories-done"
                          >
                            Done
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Image Crop Dialog - Available to Admin and Staff */}
      {canEditPhotos && (
        <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
          <DialogContent className="sm:max-w-3xl bg-white border-2 border-green-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Crop className="w-5 h-5 text-green-600" />
                Crop Image
              </DialogTitle>
              <DialogDescription>
                Drag to adjust the crop area, then click Apply Crop
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {imageToCrop && (
                <div className="flex justify-center bg-gray-100 rounded-lg p-4 max-h-[60vh] overflow-auto">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    className="max-w-full"
                  >
                    <img 
                      src={imageToCrop} 
                      alt="Crop preview" 
                      onLoad={onImageLoad}
                      style={{ maxHeight: '50vh', maxWidth: '100%' }}
                    />
                  </ReactCrop>
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={handleCancelCrop}
                className="border-gray-300"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleApplyCrop}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Check className="w-4 h-4 mr-2" />
                Apply Crop
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Mark Product Reasons Dialog */}
      {canMarkProducts && (
        <Dialog open={showMarkDialog} onOpenChange={setShowMarkDialog}>
          <DialogContent className="sm:max-w-md bg-white border-2 border-orange-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Flag className="w-5 h-5 text-orange-600" />
                Mark Product for Review
              </DialogTitle>
              <DialogDescription>
                Select the reason(s) for flagging this product
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-3">
              {['Image', 'MRP', 'Description', 'Part Number'].map((reason) => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedReasons.includes(reason)
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedReasons.includes(reason)}
                    onChange={() => toggleReason(reason)}
                    className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="font-medium text-gray-900">{reason}</span>
                </label>
              ))}
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowMarkDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={submitMark}
                disabled={selectedReasons.length === 0}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <Flag className="w-4 h-4 mr-2" />
                Mark Product
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Image Lightbox - Click to expand */}
      <ImageLightbox
        isOpen={showLightbox}
        onClose={() => setShowLightbox(false)}
        imageUrl={imageDataUrl}
        altText={part.part_name}
      />

      {/* AI Studio Modal */}
      <AIStudioModal
        isOpen={showAIStudio}
        onClose={() => {
          setShowAIStudio(false);
          setAIStudioImage(null);
        }}
        imageData={aiStudioImage}
        onComplete={(processedImage) => {
          setEditImageUrl(processedImage);
          // Convert data URL to blob for upload
          fetch(processedImage)
            .then(res => res.blob())
            .then(blob => {
              const file = new File([blob], `${part.jp_part_no || 'ai_processed'}.jpg`, { type: 'image/jpeg' });
              setNewImageFile(file);
            });
          toast.success('AI processed image ready!');
          setShowAIStudio(false);
          setAIStudioImage(null);
        }}
        token={token}
      />

      {/* Price Request Dialog */}
      <PriceRequestDialog
        isOpen={showPriceRequestDialog}
        onClose={() => setShowPriceRequestDialog(false)}
        part={{
          jp_part_no: part.jp_part_no,
          part_name: part.part_name,
          tax_paid_price: part.discounted_price || part.mrp,
          price: part.discounted_price || part.mrp
        }}
        onRequestSubmitted={() => {
          setHasPendingRequest(true);
        }}
      />
    </>
  );
};

export default PartCard;
